import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Tag, Spin, Tooltip } from "antd";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { getConsentTemplate, getConsentTemplateSourcePdfBlob, updateConsentTemplateField } from "../../apis/consent";
import { ConsentTemplate, ConsentTemplateField } from "../../interfaces/Consent";
import { useNotification } from "../../NotificationContext";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

// 8 distinct colors for up to 8 fields per page
const PIN_COLORS = [
  "#6366f1", "#f59e0b", "#10b981", "#ef4444",
  "#8b5cf6", "#06b6d4", "#f97316", "#ec4899",
];

interface LocalField extends ConsentTemplateField {
  dirty: boolean;
}

const ConsentTemplateDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [template, setTemplate] = useState<ConsentTemplate | null>(null);
  const [fields, setFields] = useState<LocalField[]>([]);
  const [loading, setLoading] = useState(true);

  // PDF state
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed
  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null); // PDF points
  const pageRef = useRef<HTMLDivElement>(null);
  const [renderWidth, setRenderWidth] = useState(560); // px

  // Interaction state
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null); // fieldId being dragged
  const [saving, setSaving] = useState<Set<number>>(new Set());

  useEffect(() => {
    const tid = Number(id);
    Promise.all([
      getConsentTemplate(tid).then((res: any) => res.data ?? res),
      getConsentTemplateSourcePdfBlob(tid),
    ])
      .then(([tpl, blob]) => {
        setTemplate(tpl);
        setFields((tpl.fields as ConsentTemplateField[]).map((f) => ({ ...f, dirty: false })));
        setPdfBlob(blob);
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      })
      .catch(() => showNotification("error", "Failed to load template", "", "topRight"))
      .finally(() => setLoading(false));
  }, [id]);

  // Keep pdfUrl clean
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  // Observe rendered width of the page container
  useEffect(() => {
    if (!pageRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setRenderWidth(w);
    });
    ro.observe(pageRef.current);
    return () => ro.disconnect();
  }, [loading]);

  const scale = pageSize ? renderWidth / pageSize.width : 1;

  const fieldsOnPage = fields.filter((f) => f.page === currentPage);

  // --- coordinate conversion helpers ---
  const pxToPt = useCallback(
    (px: number, py: number) => ({
      x: Math.round(px / scale),
      y: Math.round(py / scale),
    }),
    [scale]
  );

  // --- field update helpers ---
  const patchField = (fieldId: number, updates: Partial<LocalField>) => {
    setFields((prev) =>
      prev.map((f) => (f.id === fieldId ? { ...f, ...updates, dirty: true } : f))
    );
  };

  const saveField = async (fieldId: number) => {
    const f = fields.find((ff) => ff.id === fieldId);
    if (!f) return;
    setSaving((s) => new Set(s).add(fieldId));
    try {
      await updateConsentTemplateField(Number(id), fieldId, {
        x: f.x,
        y: f.y,
        x_max: f.xMax ?? null,
        page: f.page,
      });
      setFields((prev) =>
        prev.map((ff) => (ff.id === fieldId ? { ...ff, dirty: false } : ff))
      );
      showNotification("success", `${f.label ?? f.fieldKey} saved`, "", "topRight");
    } catch {
      showNotification("error", "Failed to save field", "", "topRight");
    } finally {
      setSaving((s) => { const n = new Set(s); n.delete(fieldId); return n; });
    }
  };

  const saveAll = async () => {
    const dirty = fields.filter((f) => f.dirty);
    await Promise.all(dirty.map((f) => saveField(f.id)));
  };

  // --- drag on PDF canvas ---
  const handlePinMouseDown = (e: React.MouseEvent, fieldId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFieldId(fieldId);
    setDragging(fieldId);
  };

  const handlePageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragging == null || !pageRef.current) return;
    const rect = pageRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const { x, y } = pxToPt(px, py);
    patchField(dragging, { x, y });
  };

  const handlePageMouseUp = () => {
    setDragging(null);
  };

  // --- click on PDF to place selected field ---
  const handlePageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragging != null || selectedFieldId == null || !pageRef.current) return;
    // only move if clicking on the background, not a pin
    if ((e.target as HTMLElement).closest("[data-pin]")) return;
    const rect = pageRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const { x, y } = pxToPt(px, py);
    patchField(selectedFieldId, { x, y });
  };

  const dirtyCount = fields.filter((f) => f.dirty).length;

  if (loading || !template) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b bg-white shrink-0">
        <div>
          <h1 className="text-base font-semibold">{template.name}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs font-mono text-indigo-600">{template.key}</span>
            <Tag className="text-xs">{template.pattern}</Tag>
            <span className="text-xs text-gray-400">{template.pageCount} page{template.pageCount !== 1 ? "s" : ""}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirtyCount > 0 && (
            <Button type="primary" onClick={saveAll}>
              Save {dirtyCount} change{dirtyCount !== 1 ? "s" : ""}
            </Button>
          )}
          <Button onClick={() => navigate(-1)}>Back</Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: PDF viewer */}
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-100">
          {/* Page tabs */}
          {numPages > 1 && (
            <div className="flex gap-1 px-3 py-2 border-b bg-white shrink-0 overflow-x-auto">
              {Array.from({ length: numPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`px-3 py-1 text-xs rounded-md border transition-colors whitespace-nowrap ${
                    currentPage === i
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Page {i + 1}
                  {fields.filter((f) => f.page === i).length > 0 && (
                    <span className="ml-1 opacity-70">
                      ({fields.filter((f) => f.page === i).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="flex-1 overflow-auto p-4">
            {pdfUrl ? (
              <div className="mx-auto" style={{ maxWidth: 700 }}>
                {/* Instruction */}
                <p className="text-xs text-gray-500 mb-2 text-center">
                  {selectedFieldId
                    ? `Click on the PDF to move "${fields.find((f) => f.id === selectedFieldId)?.label ?? "field"}" · or drag its pin`
                    : "Select a field on the right, then click the PDF to reposition it"}
                </p>

                {/* PDF page + pin overlay */}
                <div
                  ref={pageRef}
                  className="relative shadow-lg select-none"
                  style={{ cursor: dragging != null ? "grabbing" : selectedFieldId ? "crosshair" : "default" }}
                  onMouseMove={handlePageMouseMove}
                  onMouseUp={handlePageMouseUp}
                  onMouseLeave={handlePageMouseUp}
                  onClick={handlePageClick}
                >
                  <Document
                    file={pdfBlob}
                    onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                    loading={<div className="flex items-center justify-center h-48 text-gray-400 text-sm">Loading PDF…</div>}
                    error={<div className="flex items-center justify-center h-48 text-red-400 text-sm">Could not load PDF</div>}
                  >
                    <Page
                      pageIndex={currentPage}
                      width={renderWidth}
                      onLoadSuccess={(page) =>
                        setPageSize({ width: page.originalWidth, height: page.originalHeight })
                      }
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>

                  {/* Field pins */}
                  {pageSize &&
                    fieldsOnPage.map((f, idx) => {
                      const color = PIN_COLORS[idx % PIN_COLORS.length];
                      const pinX = f.x * scale;
                      const pinY = f.y * scale;
                      const isSelected = selectedFieldId === f.id;
                      const isDragging = dragging === f.id;

                      return (
                        <Tooltip
                          key={f.id}
                          title={`${f.label ?? f.fieldKey} · (${Math.round(f.x)}, ${Math.round(f.y)})`}
                          placement="top"
                        >
                          <div
                            data-pin
                            onMouseDown={(e) => handlePinMouseDown(e, f.id)}
                            onClick={(e) => { e.stopPropagation(); setSelectedFieldId(f.id); }}
                            style={{
                              position: "absolute",
                              left: pinX,
                              top: pinY,
                              transform: "translate(-50%, -50%)",
                              zIndex: isSelected ? 30 : 20,
                              cursor: isDragging ? "grabbing" : "grab",
                              userSelect: "none",
                            }}
                          >
                            {/* Pin dot */}
                            <div
                              style={{
                                width: isSelected ? 14 : 10,
                                height: isSelected ? 14 : 10,
                                borderRadius: "50%",
                                background: color,
                                border: `2px solid ${isSelected ? "white" : color}`,
                                boxShadow: isSelected
                                  ? `0 0 0 2px ${color}, 0 2px 6px rgba(0,0,0,0.3)`
                                  : "0 1px 3px rgba(0,0,0,0.3)",
                                transition: isDragging ? "none" : "all 0.1s",
                              }}
                            />
                            {/* Label tag */}
                            <div
                              style={{
                                position: "absolute",
                                left: "50%",
                                bottom: "calc(100% + 4px)",
                                transform: "translateX(-50%)",
                                background: color,
                                color: "white",
                                fontSize: 9,
                                fontWeight: 600,
                                padding: "1px 5px",
                                borderRadius: 3,
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                opacity: isSelected ? 1 : 0.75,
                              }}
                            >
                              {f.label ?? f.fieldKey}
                            </div>
                            {/* xMax line indicator */}
                            {f.xMax != null && (
                              <div
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  top: "50%",
                                  width: (f.xMax - f.x) * scale,
                                  height: 1,
                                  background: color,
                                  opacity: 0.5,
                                  pointerEvents: "none",
                                }}
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
                No source PDF found on server
              </div>
            )}
          </div>
        </div>

        {/* Right: field list */}
        <div className="w-80 shrink-0 border-l bg-white flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Fields ({template.fields.length})
            </p>
          </div>
          <div className="flex-1 overflow-y-auto divide-y">
            {fields.map((f, idx) => {
              const color = PIN_COLORS[
                fields.filter((ff) => ff.page === f.page).findIndex((ff) => ff.id === f.id) %
                  PIN_COLORS.length
              ];
              const isSelected = selectedFieldId === f.id;

              return (
                <div
                  key={f.id}
                  onClick={() => {
                    setSelectedFieldId(f.id);
                    if (f.page !== currentPage) setCurrentPage(f.page);
                  }}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    isSelected ? "bg-indigo-50" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="inline-block shrink-0 rounded-full"
                        style={{ width: 8, height: 8, background: color }}
                      />
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {f.label ?? f.fieldKey}
                      </span>
                      {f.dirty && (
                        <span className="text-xs text-amber-600 shrink-0">●</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0 ml-2">p{f.page + 1}</span>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5">
                    {(["x", "y", "xMax"] as const).map((key) => (
                      <div key={key}>
                        <label className="block text-xs text-gray-400 mb-0.5 uppercase">{key}</label>
                        <input
                          type="number"
                          value={f[key] ?? ""}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const val = e.target.value === "" ? null : Number(e.target.value);
                            if (key === "xMax") {
                              patchField(f.id, { xMax: val ?? undefined });
                            } else {
                              patchField(f.id, { [key]: val ?? 0 } as any);
                            }
                          }}
                          className="block w-full rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-900 focus:border-indigo-400 focus:outline-none"
                        />
                      </div>
                    ))}
                  </div>

                  {f.dirty && (
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); saveField(f.id); }}
                        disabled={saving.has(f.id)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
                      >
                        {saving.has(f.id) ? "Saving…" : "Save"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentTemplateDetail;
